import React, { useState } from 'react';
import { useLogin } from '@refinedev/core';
import { Form, Input, Button, Typography, Alert, Space } from 'antd';
import { LockOutlined, MailOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { LoginCredentials } from '@/types';

const { Title, Text } = Typography;

export function LoginPage(): React.ReactElement {
  const { mutate: login, isLoading } = useLogin<LoginCredentials>();
  const [error, setError] = useState<string | null>(null);

  const onFinish = (values: LoginCredentials): void => {
    setError(null);
    login(values, {
      onError: (err) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof (err as { message?: string }).message === 'string'
              ? (err as { message: string }).message
              : 'Login fehlgeschlagen.';
        setError(message);
      },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(circle at 20% 0%, rgba(99,102,241,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(14,165,233,0.3) 0%, transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          width: 420,
          borderRadius: 18,
          background: 'rgba(255,255,255,0.98)',
          boxShadow: '0 20px 60px rgba(15,23,42,0.45)',
          padding: 40,
          position: 'relative',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 12px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 28,
                boxShadow: '0 10px 30px rgba(99,102,241,0.4)',
              }}
            >
              <ThunderboltOutlined />
            </div>
            <Title level={3} style={{ marginBottom: 4 }}>
              Schnitzraum KI-Stack
            </Title>
            <Text type='secondary'>Willkommen zurück — bitte anmelden</Text>
          </div>

          {error && (
            <Alert
              message={error}
              type='error'
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ borderRadius: 10 }}
            />
          )}

          <Form layout='vertical' onFinish={onFinish} autoComplete='off' size='large'>
            <Form.Item
              name='email'
              label='E-Mail'
              rules={[
                { required: true, message: 'E-Mail eingeben' },
                { type: 'email', message: 'Ungültige E-Mail' },
              ]}
            >
              <Input prefix={<MailOutlined style={{ color: 'rgba(15,23,42,0.4)' }} />} placeholder='admin@example.com' />
            </Form.Item>

            <Form.Item name='password' label='Passwort' rules={[{ required: true, message: 'Passwort eingeben' }]}>
              <Input.Password prefix={<LockOutlined style={{ color: 'rgba(15,23,42,0.4)' }} />} placeholder='••••••••••••' />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type='primary'
                htmlType='submit'
                loading={isLoading}
                block
                size='large'
                style={{
                  height: 44,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
                }}
              >
                Anmelden
              </Button>
            </Form.Item>

            <div style={{ textAlign: 'center' }}>
              <Link to='/forgot-password' style={{ color: '#6366f1', fontSize: 14 }}>
                Passwort vergessen?
              </Link>
            </div>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              © {new Date().getFullYear()} Schnitzraum · sicher und DSGVO-konform
            </Text>
          </div>
        </Space>
      </div>
    </div>
  );
}
